import { useQuery } from "@tanstack/react-query";
import type { TreeDataNode } from "antd";
import type { DirectoryTreeProps } from "antd/lib/tree";
import { debounce } from "lodash";
import React, {
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Alert } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import type { Option } from "react-bootstrap-typeahead/types/types";
import type {
  Case,
  ExternalFolderIEnumerableExternalPartnerResponse,
} from "@/briefpoint-client";
import {
  type CombinedCaseSearch,
  mergeCases,
} from "@/components/ConfirmInfo/ReviewPageOne";
import { NoSuggestCustomMenuExternalUpload } from "@/components/ConfirmInfo/utils";
import styles from "@/components/Modals/RespondToDiscoveryModal.module.scss";
import { useCaseApi, useDocumentApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useDocuments from "@/hooks/useDocuments";
import useReInitiateAuth from "@/hooks/useExternalAuthenticate";
import type {
  ExtendedExternalFolder,
  ExtendedExternalFolderResponse,
  ExternalCaseResponse,
  ExternalDocumentResponse,
} from "@/lib/types";
import {
  externalCaseFilterCallback,
  transformSingleObjectToArray,
} from "@/lib/utils";
import {
  delayDocumentRefresh,
  findFirstFolderKey,
  handleFolderSelection,
  mergeFolderData,
  transformDataExternalToBp,
  updateFolderFlag,
} from "@/utils/ExternalPartner/utils";
import ExternalDirectoryWrapper from "./ExternalDirectoryWrapper";

const GENERAL_ERROR_MSG = "An error occurred. Please try again later.";

interface ExternalFileBrowserSelectProps {
  cases?: Case[];
  case?: Case;
  isExternalUploading: boolean;
  setIsExternalUploading: React.Dispatch<SetStateAction<boolean>>;
  onFinish: () => void;
}

export default React.memo(function ExternalFileBrowserSelect(
  props: ExternalFileBrowserSelectProps,
) {
  const [_, loadDocuments] = useDocuments();
  const { user, firm } = useAuth();
  const { errorAuthenticateMsg } = useReInitiateAuth();
  const caseApi = useCaseApi();
  const documentApi = useDocumentApi();
  const [externalLoading, setExternalLoading] = useState<boolean>(false);
  const [areFilesAvailable, setAreFilesAvailable] = useState<boolean | null>(
    null,
  );
  const [mappedExternalFolders, setMappedExternalFolders] = useState<
    ExtendedExternalFolderResponse | undefined
  >();
  const [selectedCase, setSelectedCase] = useState<Case | undefined>(
    props.case ?? undefined,
  );
  const [filesToUpload, setFilesToUpload] = useState<
    ExternalDocumentResponse[]
  >([]);
  const [selectedFolderId, setSelectedFolderId] = useState<
    string | undefined
  >();
  const [firmCases, setFirmCases] = useState<
    ExternalCaseResponse[] | undefined
  >();
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  // this is used to prevent the typeahead from being invalid on render
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);

  // QUERIES
  // Query for loading external case folders
  const folderQuery =
    useQuery<ExternalFolderIEnumerableExternalPartnerResponse>({
      queryKey: [
        "caseApi.caseGetExternalCaseFolderRaw",
        selectedCase?.integration?.identifier,
        user?.firmId,
        selectedFolderId,
      ],
      queryFn: async () => {
        try {
          // Use the raw API method to get the response before deserialization
          const rawResponse = await caseApi.caseGetExternalCaseFolderRaw({
            externalCaseId: selectedCase?.integration?.identifier || "",
            firmId: user?.firmId || "",
            folderId: selectedFolderId || "",
          });

          // Get the raw JSON response
          const jsonData = await rawResponse.raw.json();

          // Transform the response to handle the actual API structure
          // The API returns data as a single ExternalFolder object, but the type expects an array
          return transformSingleObjectToArray(jsonData);
        } catch (error) {
          console.error("Error fetching external case folder:", error);
          throw error;
        }
      },
      enabled: !!selectedCase && !!selectedCase?.integration?.identifier,
    });

  // Query for loading external cases
  const externalCasesQuery = useQuery({
    queryKey: ["caseApi.caseGetExternal", user?.firmId],
    queryFn: async () => {
      if (!user) {
        throw new Error("User not available");
      }

      const getExternalCases = await caseApi.caseGetExternal({
        firmId: user.firmId || "",
      });
      setFirmCases(getExternalCases.data as ExternalCaseResponse[]);
      return getExternalCases.data || [];
    },
    enabled: !!user,
  });

  // Handle folder query success/error and merge data
  useEffect(() => {
    if (folderQuery.isSuccess && folderQuery.data) {
      const hasFolders =
        folderQuery.data.data && folderQuery.data.data.length > 0;
      setAreFilesAvailable(!!hasFolders);

      // Merge the new data with existing data
      setMappedExternalFolders((prev) => {
        const existingData = prev?.data ?? [];
        const newData = folderQuery.data?.data ?? [];

        const mergedData = mergeFolderData(
          existingData,
          newData,
          selectedFolderId,
        );

        // Update flag for selected folder if it exists in existing data
        const updatedData = updateFolderFlag(
          mergedData as ExtendedExternalFolder[],
          selectedFolderId,
        );

        return {
          ...folderQuery.data,
          data: updatedData,
        };
      });
    }
  }, [folderQuery.isSuccess, folderQuery.data, selectedFolderId]);

  if (folderQuery.isError) {
    setAreFilesAvailable(false);
  }

  const handleFilterChange = useCallback(
    async (filter?: string) => {
      if (user?.externalConnection?.isActive) {
        setExternalLoading(true);
        try {
          const getExternalCases = await caseApi.caseGetExternal({
            firmId: user.firmId || "",
            filter,
          });
          setFirmCases(getExternalCases.data as ExternalCaseResponse[]);
        } catch (error) {
          throw new Error(error as string);
        } finally {
          setExternalLoading(false);
        }
      }
    },
    [caseApi, user],
  );

  const debouncedFilter = useMemo(
    () =>
      debounce(async (filter?: string) => {
        await handleFilterChange(filter);
      }, 400),
    [handleFilterChange],
  );

  const [treeData, dataMap, folderKeys] = transformDataExternalToBp(
    mappedExternalFolders?.data || [],
    folderQuery.isLoading,
  ) as [TreeDataNode[], Map<string, ExternalDocumentResponse>, Set<string>];

  // Auto-expand the first folder when tree data is first available
  useEffect(() => {
    if (treeData.length > 0 && expandedKeys.length === 0) {
      const firstFolderKey = findFirstFolderKey(treeData);
      if (firstFolderKey) {
        setExpandedKeys([firstFolderKey]);
      }
    }
  }, [treeData, expandedKeys.length]);

  if (!user || !firm) {
    return null;
  }

  if (!user.externalConnection?.isActive) {
    return <div>No external connection... you shouldn't really be here.</div>;
  }

  const onSelect: DirectoryTreeProps["onSelect"] = (selectedKeys) => {
    const files = selectedKeys
      .map((key) =>
        (dataMap as Map<string, ExternalDocumentResponse>).get(key.toString()),
      )
      .filter((file): file is ExternalDocumentResponse => !!file);

    handleFolderSelection(
      selectedKeys,
      folderKeys,
      mappedExternalFolders?.data ?? undefined,
      expandedKeys,
      setExpandedKeys,
      setSelectedFolderId,
    );

    setFilesToUpload(files);
  };

  const onExpand: DirectoryTreeProps["onExpand"] = (
    expandedKeysValue,
    info,
  ) => {
    setExpandedKeys(expandedKeysValue);

    // When a folder's switcher icon is clicked, trigger the same selection logic as clicking the folder
    // But only when expanding, not when collapsing
    if (info.node) {
      const nodeKey = info.node.key;
      const nodeKeyString = nodeKey?.toString();

      // Check if this is a folder that was just expanded (not collapsed)
      const wasJustExpanded =
        expandedKeysValue.includes(nodeKey) && !expandedKeys.includes(nodeKey);

      // Only trigger selection logic when expanding a folder
      if (wasJustExpanded && nodeKeyString && folderKeys.has(nodeKeyString)) {
        // Trigger selection logic when switcher is clicked on a folder
        handleFolderSelection(
          [nodeKey],
          folderKeys,
          mappedExternalFolders?.data ?? undefined,
          expandedKeysValue,
          setExpandedKeys,
          setSelectedFolderId,
        );

        // Also select files if any exist (though folders typically won't have files in dataMap)
        const files = [nodeKey]
          .map((key) =>
            (dataMap as Map<string, ExternalDocumentResponse>).get(
              key.toString(),
            ),
          )
          .filter((file): file is ExternalDocumentResponse => !!file);

        if (files.length > 0) {
          setFilesToUpload(files);
        }
      }
    }
  };

  const doFileUpload = async () => {
    props.setIsExternalUploading(true);
    const getUploadLinks = await caseApi.caseGetExternalCaseFileDownloadLinks({
      firmId: firm.id,
      externalCaseId: selectedCase?.integration?.identifier || "",
      externalDocument: filesToUpload,
    });

    if (
      getUploadLinks &&
      getUploadLinks.responseCode === 200 &&
      getUploadLinks.data?.length
    ) {
      // Create a new case linked to BP where the external case is does NOT have an internal ID with us (IE: Not linked to BP).
      if (!selectedCase?.id) {
        const newCase = await caseApi.casePost({
          firmId: user.firmId || "",
          _case: selectedCase,
        });
        await caseApi.caseExternalIntegration({
          firmId: user.firmId || "",
          id: newCase.id || "",
          externalIntegration: newCase?.integration,
        });
        await documentApi.documentExternalFiles({
          caseId: newCase.id || "",
          externalFile: getUploadLinks.data.map((d) => {
            return {
              location: d.location || "",
              size: d.size || 0,
              name: d.name?.endsWith(d.type ?? "")
                ? d.name
                : `${d.name}${d.type}`,
            };
          }),
        });
      } else {
        // If the selected external case already exists with us however, we just use the associated case Id linked w/ it and upload...
        await documentApi.documentExternalFiles({
          caseId: selectedCase?.id,
          externalFile: getUploadLinks.data.map((d) => {
            return {
              location: d.location || "",
              size: d.size || 0,
              name: d.name?.endsWith(d.type ?? "")
                ? d.name
                : `${d.name}${d.type}`,
            };
          }),
        });
      }
    } else {
      props.setIsExternalUploading(false);
    }

    delayDocumentRefresh(
      2750,
      loadDocuments,
      props.setIsExternalUploading,
      props.onFinish,
    );
  };

  const mergedCases = mergeCases(props.cases, firmCases);

  function handleCaseChange(e: Option[]): void {
    const val = e[0];

    // TODO: Possibly refactor this so this modal isn't dependant on a merged case list and should only use the firmCases list (Which has external cases only)?
    // We'd have to reformat the _case structure below obviously, or we can make this and the SDR into a utility function these two components could use...

    if (val) {
      const c = val as CombinedCaseSearch;
      let _case: Case | undefined = props.cases?.find(
        (x) =>
          x.id === c.internalId && x.integration?.identifier === c.externalId,
      );

      if (!_case) {
        const integration = c.externalId
          ? { partner: c.partner, identifier: c.externalId }
          : undefined;
        _case = {
          title: c.title,
          shortTitle: c.title,
          integration,
        };
      }
      setSelectedCase(_case);
    } else {
      setSelectedCase(undefined);
    }
    setAreFilesAvailable(null);
    setMappedExternalFolders(undefined);
    setSelectedFolderId(undefined);
    setExpandedKeys([]); // Reset expanded keys so first folder auto-expands for new case
  }

  return (
    <ExternalDirectoryWrapper
      multiple
      isSubmitted={props.isExternalUploading}
      selectedCase={selectedCase}
      onSelect={onSelect}
      expandedKeys={expandedKeys}
      onExpand={onExpand}
      files={filesToUpload}
      isLoading={folderQuery.isLoading}
      directoryTitle="Upload Document"
      closeModal={props.onFinish}
      submitActionText="Upload"
      submitAction={doFileUpload}
      treeData={treeData as TreeDataNode[]}
      areFilesAvailable={areFilesAvailable}
    >
      <div className="mb-3">
        {(errorAuthenticateMsg ||
          folderQuery.isError ||
          externalCasesQuery.isError) && (
          <Alert variant="danger">
            {errorAuthenticateMsg || GENERAL_ERROR_MSG}
          </Alert>
        )}
        {!props.case && firmCases && !externalCasesQuery.isLoading ? (
          <Typeahead
            className={
              user.externalConnection.isActive &&
              selectedCase?.integration?.partner
                ? styles[`external-partner-${selectedCase.integration.partner}`]
                : ""
            }
            onChange={(e) => {
              handleCaseChange(e);
              setHasUserInteracted(true);
            }}
            onInputChange={(f) => {
              debouncedFilter(f);
              setHasUserInteracted(true);
            }}
            onBlur={() => {
              setHasUserInteracted(true);
            }}
            selected={(selectedCase && [selectedCase]) || []}
            isInvalid={!selectedCase && hasUserInteracted}
            id="case"
            emptyLabel="No matches found"
            highlightOnlyResult
            filterBy={(option, props) =>
              externalCaseFilterCallback(option, props, user, firmCases)
            }
            options={mergedCases.filter((c) => c.partner)}
            renderMenu={(results, menuProps, state) => (
              <NoSuggestCustomMenuExternalUpload
                results={results as CombinedCaseSearch[]}
                menuProps={menuProps}
                state={state}
                showSourceLogos={!!user.externalConnection?.isActive}
              />
            )}
            placeholder="Find a case..."
            labelKey="title"
            isLoading={externalLoading || externalCasesQuery.isLoading}
          />
        ) : (
          !props.case && <div>Loading cases...</div>
        )}
      </div>
    </ExternalDirectoryWrapper>
  );
});
